import { Box, Typography, useTheme } from '@mui/material';
import { FC } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { List as VirtualizedList, RowComponentProps } from 'react-window';
import { useInfiniteLoader } from 'react-window-infinite-loader';

import { User, UserMin } from '@apiContract';

import { sxList, sxNoResult } from '../../styles';
import Item from './Item';

type ListProps = {
  loadNextPage: (startIndex: number, stopIndex: number) => void | Promise<void>;
  isNextPageLoading: boolean;
  items: Array<UserMin>;
  setSelectedUser: (user: UserMin) => void;
  selectedUser?: User | UserMin | null;
  hasNextPage: boolean;
  changeTab: () => void;
  darkMode?: boolean;
};

const UserItem = ({ style, index, changeTab, selectedUser, items, setSelectedUser, isNextPageLoading }: RowComponentProps<{
  changeTab: () => void,
  selectedUser?: User | UserMin | null,
  items: Array<UserMin>,
  setSelectedUser: (user: UserMin) => void,
  isNextPageLoading: boolean,
}>) => (
  <>
    <Item
      changeTab={changeTab}
      style={style}
      selected={!!selectedUser && !!items[index] && items[index]?.id === selectedUser?.id}
      item={items[index]}
      handleClick={() => items[index] && setSelectedUser(items[index])}
    />
    {items.length === 0 && !isNextPageLoading ? (
      <Typography sx={sxNoResult} variant="body2">
        No results found
      </Typography>
    ) : null}
  </>
);

const List: FC<ListProps> = ({
  loadNextPage,
  isNextPageLoading,
  items,
  setSelectedUser,
  selectedUser,
  hasNextPage,
  changeTab,
  darkMode = false,
}) => {
  const theme = useTheme();

  const rowCount = hasNextPage ? items.length + 1 : items.length + 1;
  const loadMoreRows = isNextPageLoading ? () => {} : loadNextPage;
  const isRowLoaded = (index: number) => !hasNextPage || index < items.length;
  const onRowsRendered = useInfiniteLoader({
    isRowLoaded,
    loadMoreRows: loadMoreRows as unknown as (startIndex: number, stopIndex: number) => Promise<void>,
    rowCount,
    threshold: 50,
  });

  return (
    <Box sx={sxList(theme, darkMode)}>
      <AutoSizer>
        {({ height, width }) => {
          return (
            <VirtualizedList
              style={{ height, width, maxHeight: 'inherit' }}
              onRowsRendered={onRowsRendered}
              rowCount={rowCount}
              rowHeight={60}
              rowProps={{
                changeTab,
                selectedUser,
                items,
                setSelectedUser,
                isNextPageLoading,
              }}
              className="customScrollBar"
              rowComponent={UserItem}
            />
          );
        }}
      </AutoSizer>
    </Box>
  );
};

export default List;
